import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import { inputVariants } from '@/app/common/components/input/inputVariants'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { zodResolver } from '@hookform/resolvers/zod'
import { MutationStatus } from '@tanstack/react-query'
import CurrencyInput from 'react-currency-input-field'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { FundingSource } from '../../schema'
import { Wallet } from 'lucide-react'
// Zod schema for purse creation
const CreatePurseSchema = z.object({
  name: z.string().min(1, 'Purse name is required'),
  source_type: z.string().min(1, 'Source type is required'),
  sub_source_type: z.string().min(1, 'Sub source is required'),
  achMid: z.string().min(1),
  total: z
    .string()
    .min(1, 'Funded amount is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
      message: 'Funded amount must be a positive number',
    })
    .refine((val) => Number(val) <= 100000000, {
      message: 'Funded amount cannot exceed $100,000,000',
    }),
})
export type CreatePurseFormValues = z.infer<typeof CreatePurseSchema>

interface CreatePurseDialogProps {
  open: boolean
  fundingSources: FundingSource[]
  onOpenChange: (open: boolean) => void
  onCreatePurse: (purse: CreatePurseFormValues) => void
  mutationStatus: MutationStatus
}

export function CreatePurseDialog({
  open,
  onOpenChange,
  onCreatePurse,
  fundingSources,
  mutationStatus,
}: CreatePurseDialogProps) {
  // Remove useState for selectedSourceId
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreatePurseFormValues>({
    resolver: zodResolver(CreatePurseSchema),
    defaultValues: {
      name: '',
      source_type: '',
      sub_source_type: '',
      total: '',
      achMid: '',
    },
  })

  // Watch the source_type field to determine the selected source
  const selectedSourceId = watch('source_type')

  const onSubmit = (data: CreatePurseFormValues) => {
    onCreatePurse({
      ...data,
    })
  }

  // Find selected funding source for sub source options
  const selectedSource = fundingSources.find(
    (src) => String(src.id) === selectedSourceId
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Purse</DialogTitle>
          <DialogDescription>
            Add a new program purse to manage scholarship funding.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit, (error) => {
            console.log(error)
          })}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Purse Name</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input id="name" {...field} placeholder="Enter purse name" />
              )}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>
          {/* Purse Source Select */}
          <div className="space-y-2">
            <Label htmlFor="source_type">Purse Source</Label>
            <Controller
              name="source_type"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(val) => {
                    field.onChange(val)
                    setValue('sub_source_type', '')
                  }}
                >
                  <SelectTrigger className="shadow-xs">
                    <SelectValue placeholder="Select purse source" />
                  </SelectTrigger>
                  <SelectContent>
                    {fundingSources.map((src) => (
                      <SelectItem key={src.id} value={String(src.id)}>
                        {src.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.source_type && (
              <p className="text-sm text-red-500">
                {errors.source_type.message}
              </p>
            )}
          </div>
          {/* Sub Source Select */}
          <div className="space-y-2">
            <Label htmlFor="sub_source_type">Sub Source</Label>
            <Controller
              name="sub_source_type"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={
                    !selectedSource || selectedSource.sub_types.length === 0
                  }
                >
                  <SelectTrigger className="shadow-xs">
                    <SelectValue placeholder="Select sub source" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedSource && selectedSource.sub_types.length > 0
                      ? selectedSource.sub_types.map((sub) => (
                          <SelectItem key={sub.id} value={String(sub.id)}>
                            {sub.name}
                          </SelectItem>
                        ))
                      : null}
                  </SelectContent>
                </Select>
              )}
            />
            {(!selectedSource || selectedSource.sub_types.length === 0) && (
              <p className="text-sm text-muted-foreground">
                No sub sources available
              </p>
            )}
            {errors.sub_source_type && (
              <p className="text-sm text-red-500">
                {errors.sub_source_type.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="achMid">ACH Mid</Label>
            <Controller
              name="achMid"
              control={control}
              render={({ field }) => (
                <Input id="achMid" {...field} placeholder="Enter ACH Mid" />
              )}
            />
            {errors.achMid && (
              <p className="text-sm text-red-500">{errors.achMid.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="total">Funded Amount</Label>
            <Controller
              name="total"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  className={inputVariants({
                    variant: 'text',
                  })}
                  id="total"
                  decimalsLimit={2}
                  min={0}
                  prefix="$"
                  placeholder="Enter amount"
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            />
            {errors.total && (
              <p className="text-sm text-red-500">{errors.total.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <SubmitButton buttonTxt={'Create Purse'} status={mutationStatus}>
              Create Purse <Wallet />
            </SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
